<script setup lang="ts">
import { isAxiosError } from 'axios';
import { computed } from 'vue';

import type { FormalizationApi } from '~/services/formalization-api/formalization-api.types';

// eslint-disable-next-line import/order
import InfoGridItem from '~/components/shared/InfoGridItem.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import useInstallments from '~/store/installments/useInstallments';
import money from '~/utils/money';

interface Props {
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();

const financingId = computed(() => props.financing.id);
const installmentsQuery = useInstallments(financingId);

const installmentsData = computed(() => installmentsQuery.data.value ?? null);
const isInstallmentsLoading = computed(
  () => installmentsQuery.isLoading.value || installmentsQuery.isFetching.value,
);
const installmentsError = computed(() => installmentsQuery.error.value);
const isInstallmentsError = computed(() => installmentsQuery.isError.value);

const isNotFoundError = computed(() => {
  if (!isInstallmentsError.value || !installmentsError.value) return false;
  return isAxiosError(installmentsError.value) && installmentsError.value.response?.status === 404;
});

const isLoading = computed(() => isInstallmentsLoading.value);

const installments = computed(() => installmentsData.value?.installments ?? []);
const billingStatus = computed(() => installmentsData.value?.status ?? null);

const totalInstallments = computed(() => installments.value.length);

const paidInstallments = computed(
  () => installments.value.filter((installment) => installment.status === 'paid').length,
);

const billingStatusLabel = computed(() => {
  const status = billingStatus.value;
  if (!status) return null;

  const statusMap: Record<FormalizationApi.FinancingStatus, string> = {
    active: 'Ativo',
    cancelled: 'Cancelado',
    finished: 'Finalizado',
    grace_period: 'Período de Carência',
  };

  return statusMap[status] || status;
});

const billingStatusColor = computed<'danger' | 'info' | 'primary' | 'success' | 'warning'>(() => {
  const status = billingStatus.value;
  if (!status) return 'info';

  const colorMap: Record<FormalizationApi.FinancingStatus, 'danger' | 'info' | 'primary' | 'success' | 'warning'> = {
    active: 'success',
    cancelled: 'danger',
    finished: 'info',
    grace_period: 'warning',
  };

  return colorMap[status] || 'info';
});

const installmentValue = computed(() => {
  const firstInstallment = installments.value[0];
  if (!firstInstallment?.amount) return null;

  return money.formatBRL(firstInstallment.amount / 100);
});
</script>

<template>
  <div class="billing-content">
    <div v-if="!isLoading && isNotFoundError" class="empty-state">
      <p class="empty-message">Dados não foram encontrados</p>
    </div>

    <div v-else class="datagrid">
      <InfoGridItem
        label="Parcelas Total"
        :value="totalInstallments"
        :is-loading="isLoading"
      />

      <InfoGridItem
        label="Parcelas Pagas"
        :value="paidInstallments"
        :is-loading="isLoading"
      />

      <InfoGridItem
        label="Status da Cobrança"
        :value="billingStatusLabel"
        :is-tag="true"
        :tag-color="billingStatusColor"
        :is-loading="isLoading"
      />

      <InfoGridItem
        label="Valor de parcela"
        :value="installmentValue"
        :is-loading="isLoading"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.billing-content {
  .datagrid {
    display: grid;
    gap: 0.75rem;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
  }

  .empty-message {
    color: #94a3b8;
    font-size: 0.9375rem;
    font-style: italic;
    margin: 0;
  }
}
</style>
